import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { isAlreadyCheckedIn, useCheckInCentro } from '@/hooks/useCentroVisits';
import { checkInErrorMessage } from '@/lib/errors';

type Status =
  | { kind: 'working' }
  | { kind: 'invalid' }
  | { kind: 'blocked' }
  | { kind: 'failed'; message: string };

/**
 * Landing page for the QR code printed at a Centro's entrance.
 *
 * The code is a deep link (…/checkin/:id) rather than an opaque token, so one
 * sticker serves everybody: a phone camera opens this route directly, while the
 * in-app scanner reads the same code and posts it to the check-in endpoint.
 *
 * The route sits behind ProtectedRoute, so a participant who is not signed in
 * is sent to the login screen with this location in `state.from` and is
 * returned here — check-in then completes on its own, with no second scan.
 */
export default function CheckInPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const checkIn = useCheckInCentro();
  const [status, setStatus] = useState<Status>({ kind: 'working' });

  // Guards against the effect firing twice (React StrictMode double-invoke),
  // which would send two check-in requests for a single scan.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const digitalCenterId = Number(id);
    if (!Number.isInteger(digitalCenterId) || digitalCenterId <= 0) {
      setStatus({ kind: 'invalid' });
      return;
    }

    void (async () => {
      try {
        await checkIn.mutateAsync({ digitalCenterId });
        // The home screen is what shows the active visit, so land there.
        navigate('/', { replace: true });
      } catch (err) {
        if (isAlreadyCheckedIn(err)) {
          setStatus({ kind: 'blocked' });
          return;
        }
        setStatus({ kind: 'failed', message: checkInErrorMessage(err) });
      }
    })();
    // Runs once on mount; the ref above is the real guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="px-4 py-10">
      <Helmet>
        <title>Check-in · DIGGER</title>
      </Helmet>

      {status.kind === 'working' && (
        <div className="flex flex-col items-center gap-4 text-center" role="status">
          <span
            aria-hidden="true"
            className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-[#512f89]"
          />
          <p className="text-sm text-muted-foreground">A fazer check-in no Centro…</p>
        </div>
      )}

      {status.kind !== 'working' && (
        <div className="flex flex-col items-center gap-4 text-center">
          <p role="alert" className="rounded-xl border border-busy/50 bg-busy-soft p-3 text-sm text-busy">
            {status.kind === 'invalid' &&
              'Código de Centro inválido. Verifique o QR à entrada e tente novamente.'}
            {status.kind === 'blocked' &&
              'Já tem uma visita ativa noutro Centro. Termine essa visita antes de fazer um novo check-in.'}
            {status.kind === 'failed' && status.message}
          </p>
          <Link
            to="/"
            replace
            className="rounded-xl bg-[#512f89] px-4 py-2 text-sm font-bold text-white transition-transform active:scale-[0.99]"
          >
            Ir para o início
          </Link>
        </div>
      )}
    </div>
  );
}
